import type { ProgrammaticLandingPageConfig } from '../../types'
import { KWP_SIZE_FACTS } from '../kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[10]

export const KWP_10_INSTALACJA_FOTOWOLTAICZNA: ProgrammaticLandingPageConfig = {
  slug: 'instalacja-fotowoltaiczna-10-kwp-cena',
  lang: 'pl',
  topicId: 'kwp-kosten-10',
  title: 'Instalacja fotowoltaiczna 10 kWp: cena i wartości orientacyjne',
  metaDescription:
    'Ile kosztuje instalacja fotowoltaiczna 10 kWp? Powierzchnia dachu, liczba paneli, ' +
    'rozbicie kosztów i bezpłatne porównanie cen dla najpopularniejszej wielkości rodzinnej.',
  keywords: [
    'instalacja fotowoltaiczna 10 kWp cena',
    'panele słoneczne 10 kWp koszt',
    'ile kosztuje instalacja 10 kWp',
    '10 kWp instalacja fotowoltaiczna dom',
  ],
  eyebrow: 'Wielkość instalacji: 10 kWp',
  h1: 'Instalacja fotowoltaiczna 10 kWp: cena i wartości orientacyjne',
  intro:
    '10 kWp to jedna z najczęściej wybieranych wielkości instalacji dla domów jednorodzinnych w ' +
    'Niemczech - między innymi dlatego, że do tej granicy obowiązuje odrębna, wyższa taryfa za ' +
    'nadwyżki energii (EEG). Znajdziesz tu wartości orientacyjne dotyczące powierzchni dachu i ' +
    'liczby paneli, rozbicie kosztów, a tuż poniżej kalkulator z już wypełnioną średnią ceną dla ' +
    '10 kWp, obliczoną na podstawie prawdziwych ofert firm rzemieślniczych.',
  calculatorPrefill: { kind: 'kwp', kwp: 10 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Dla kogo odpowiednia jest instalacja 10 kWp?' },
    {
      type: 'paragraph',
      text:
        'Wartość orientacyjna: 10 kWp zwykle pasuje do gospodarstwa 4-5-osobowego lub klasycznego ' +
        'domu jednorodzinnego z rocznym zużyciem ok. 5 000-7 000 kWh - często w połączeniu z ' +
        'magazynem energii, by zwiększyć autokonsumpcję.',
    },
    { type: 'heading', text: 'Dach i panele: czego potrzebuje instalacja 10 kWp' },
    {
      type: 'paragraph',
      text:
        `Wartość orientacyjna: ok. ${FACTS.roofAreaM2} m² powierzchni dachu i ok. ` +
        `${FACTS.moduleCount} paneli przy typowych modułach ~440 Wp. Roczna produkcja przy ` +
        `orientacji południowej bez zacienienia wynosi ok. ${FACTS.annualYieldKwh.toLocaleString('pl-PL')} kWh. ` +
        'Dokładne obliczenie dla Twojego dachu daje bezpłatny kalkulator doboru instalacji.',
    },
    { type: 'heading', text: 'Składniki kosztów instalacji fotowoltaicznej' },
    {
      type: 'list',
      items: [
        'Panele: największa pojedyncza pozycja kosztowa, zwykle około jednej trzeciej całości',
        'Falownik: przy 10 kWp jedno- lub trójfazowy, zależnie od przyłącza domu',
        'Montaż/robocizna: mocowanie na dachu, okablowanie',
        'Rusztowanie: zależnie od wysokości i dostępności dachu',
        'Instalacja elektryczna: dostosowanie rozdzielnicy, podłączenie do sieci domowej',
        'Zgłoszenie: rejestracja u operatora sieci i w odpowiednich rejestrach',
      ],
    },
    {
      type: 'paragraph',
      text:
        '10 kWp to dodatkowo istotna granica prawna w Niemczech: do tej wielkości obowiązuje ' +
        'wyższa taryfa za nadwyżki energii (EEG) - dla kalkulacji ceny samej instalacji nie ma to ' +
        'jednak znaczenia, tu liczy się nadal cena za kWp.',
    },
    { type: 'heading', text: 'Amortyzacja i autokonsumpcja' },
    {
      type: 'paragraph',
      text:
        'Z odpowiednio dobranym magazynem energii udział autokonsumpcji w instalacji 10 kWp może ' +
        'znacznie przekroczyć wartość bez magazynu (zwykle ok. 25-30 %). W Niemczech do 10 kWp ' +
        'obowiązuje obecnie taryfa 7,78 ct/kWh za nadwyżki energii (stan na luty 2026, spada do ' +
        '7,71 ct/kWh od 1 sierpnia 2026). Dokładne obliczenie dla Twojego dachu daje bezpłatny ' +
        'kalkulator doboru instalacji.',
    },
  ],
  faq: [
    {
      q: 'Dlaczego 10 kWp to tak popularna wielkość instalacji?',
      a: 'Z jednej strony pasuje do typowego zużycia w domu jednorodzinnym, z drugiej - w Niemczech do 10 kWp obowiązuje wyższa taryfa za nadwyżki energii (EEG) niż powyżej tej granicy.',
    },
    {
      q: 'Czy powyżej 10 kWp zmienia się coś w taryfie za nadwyżki energii?',
      a: 'Tak, powyżej 10 kWp obowiązuje inna stawka za energię oddawaną do sieci w niemieckim systemie EEG. Dla porównania ceny samej instalacji jest to niezależne od wysokości taryfy.',
    },
    {
      q: 'Czy 10 kWp wystarczy dla domu jednorodzinnego z magazynem energii?',
      a: 'W większości przypadków tak - 10 kWp to jedna ze standardowych wielkości dla domów jednorodzinnych z magazynem energii. Przy dodatkowej pompie ciepła lub samochodzie elektrycznym warto rozważyć 12 kWp lub więcej.',
    },
  ],
  primaryCta: { label: 'Poproś teraz o bezpłatną ofertę', action: 'customer' },
  secondaryCta: {
    label: 'Koszt instalacji fotowoltaicznej: pełny przewodnik',
    action: 'link',
    href: '/koszt-instalacji-fotowoltaicznej',
  },
  relatedLinks: [
    { label: 'Instalacja fotowoltaiczna 8 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-8-kwp-cena' },
    { label: 'Instalacja fotowoltaiczna 12 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-12-kwp-cena' },
    { label: 'Instalacja fotowoltaiczna 15 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-15-kwp-cena' },
    { label: 'Magazyn energii 10 kWh: cena i wartości orientacyjne', href: '/magazyn-energii-10-kwh-cena' },
    { label: 'Jak działa porównanie ofert', href: '/porownaj-oferty-fotowoltaiczne' },
  ],
}
